import React, {
  useState,
  useCallback,
} from 'react';
import { ThemeType } from '@reverbdotcom/cadence/components/types';
import { getItem, setItem } from '../local_storage_utils';

export interface ThemeContextType {
  theme: ThemeType;
  updateTheme: (newTheme: ThemeType) => void;
}

const ThemeContext = React.createContext<ThemeContextType>({
  theme: ThemeType.default,
  updateTheme: () => {},
});

/**
 *  Please reach out to #cadence if you have any questions or concerns with this component.
 */
export function ThemeContextProvider({ children }) {
  const contextValue = useThemeContextProvider();

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return React.useContext(ThemeContext);
}

function useThemeContextProvider(): ThemeContextType {
  const [theme, setTheme] = useState<ThemeType>(ThemeType.default);

  React.useLayoutEffect(() => {
    const localStorageKey = getItem('reverb-theme') as ThemeType;
    const isValidKey = Object.keys(ThemeType).includes(localStorageKey);

    // Override legacy web / web2 themes
    if (!isValidKey) {
      return updateTheme(ThemeType.default);
    }

    return updateTheme(localStorageKey);
  }, []);

  const updateTheme = useCallback((newTheme: ThemeType) => {
    setTheme(newTheme);
    setElementAttribute(newTheme);
    setItem('reverb-theme', newTheme);
  }, [setTheme]);

  return { theme, updateTheme };
}

function setElementAttribute(themeName) {
  const htmlTag = document.getElementsByTagName('html')?.item(0);
  if (!htmlTag) return;

  htmlTag.setAttribute('data-theme', themeName);
}
